/**
 * "Smart drops" for the setup wizard: work out what a dropped / chosen file is, so it can go to the right
 * place (game jar, mod jar, resource pack, world zip, world folder, plan file, schematic) instead of failing
 * with a cryptic error. Only the zip's central directory is read — never the whole file.
 */
import { SCHEMATIC_EXTENSIONS, ZipReader } from "@mcp/world";

export type DropKind = "game-jar" | "mod-jar" | "pack" | "world-zip" | "plan" | "schematic" | "folder" | "unknown";

export interface Classified {
  kind: DropKind;
  file: File;
}

const MOD_NAME = /fabric|forge|quilt|sodium|iris|lithium|optifine|indium|modmenu|jei|rei[-_]|journeymap|xaero|create|api[-_.]/i;

/** A dropped directory shows up as an extension-less File with no type (Chrome: size 0 or 4096). */
export function looksLikeFolder(f: File) {
  return !f.type && !/\.[a-z0-9]{2,12}$/i.test(f.name) && (f.size === 0 || f.size % 4096 === 0);
}

export async function classify(file: File): Promise<DropKind> {
  const name = file.name.toLowerCase();
  if (name.endsWith(".mcplan")) return "plan";
  if (SCHEMATIC_EXTENSIONS.some((x) => name.endsWith(x))) return "schematic";
  if (looksLikeFolder(file)) return "folder";
  if (!name.endsWith(".jar") && !name.endsWith(".zip")) return "unknown";
  let entries: string[] = [];
  try {
    entries = [...(await ZipReader.open(file)).entries.keys()];
  } catch {
    return name.endsWith(".jar") ? (MOD_NAME.test(name) ? "mod-jar" : "game-jar") : "unknown";
  }
  const has = (re: RegExp) => entries.some((e) => re.test(e));
  if (has(/(^|\/)level\.dat$/)) return "world-zip";
  if (has(/^(fabric\.mod\.json|quilt\.mod\.json|META-INF\/(neo)?forge\.mods\.toml|META-INF\/mods\.toml|mcmod\.info)$/)) return "mod-jar";
  // the client jar is also the built-in resource pack (it has a pack.mcmeta too), so check it before packs
  if (name.endsWith(".jar") && has(/^assets\/minecraft\/(blockstates|models)\//)) return "game-jar";
  if (has(/^pack\.mcmeta$/) || has(/^assets\/[^/]+\/textures\//)) return name.endsWith(".jar") ? "mod-jar" : "pack";
  return name.endsWith(".jar") ? (MOD_NAME.test(name) ? "mod-jar" : "game-jar") : "unknown";
}

export async function classifyAll(files: File[]): Promise<Classified[]> {
  return Promise.all(files.map(async (file) => ({ file, kind: await classify(file) })));
}

/**
 * A folder dropped from the file manager: read every file inside it (paths only — the contents stay on disk
 * until a worker reads them). Returns null if the drop holds no folder.
 */
export async function droppedFolder(dt: DataTransfer): Promise<{ name: string; files: { path: string; file: Blob }[] } | null> {
  const items = [...dt.items].filter((i) => i.kind === "file");
  const entry = items.map((i) => (i as DataTransferItem & { webkitGetAsEntry?: () => FileSystemEntry | null }).webkitGetAsEntry?.()).find((e) => e?.isDirectory);
  if (!entry) return null;
  const files: { path: string; file: Blob }[] = [];
  const readDir = (dir: FileSystemDirectoryEntry) => new Promise<FileSystemEntry[]>((resolve, reject) => {
    const reader = dir.createReader();
    const all: FileSystemEntry[] = [];
    const next = () => reader.readEntries((batch) => { if (!batch.length) resolve(all); else { all.push(...batch); next(); } }, reject);
    next();
  });
  const walk = async (e: FileSystemEntry, prefix: string): Promise<void> => {
    if (e.isFile) {
      const file = await new Promise<File>((resolve, reject) => (e as FileSystemFileEntry).file(resolve, reject));
      files.push({ path: prefix + e.name, file });
    } else if (e.isDirectory) {
      for (const child of await readDir(e as FileSystemDirectoryEntry)) await walk(child, prefix + e.name + "/");
    }
  };
  await walk(entry, "");
  return { name: entry.name, files };
}
